// MACHINERY V2 (Stroje) — the katalog detail's below-1280 pieces (M2.16c).
//
// Rendered truth: mobile/screenshots/17-katalog-detail.png. Reference impl:
// mobile/machinery-mobile-katalog.jsx `MbKatalogDetail` — VALUES taken, code
// not. Split from CatalogDetailMobile.tsx so that file stays composition.
//
// WHAT LIVES HERE: the back-link band, the collapsible section shell, the
// key-parameter row, and the sticky CTA with its scrim.
//
// ============================================================
//  >>> THE STICKY CTA MUST BE CORRECT WITH NO ANIMATION EVER RUNNING <<<
// ============================================================
// `machinery-v2-tokens.scss:289-292` forces `animation: none !important;
// transition: none !important` on `.machinery-v2-root` AND every descendant
// under `prefers-reduced-motion: reduce`. Nothing below animates, transitions or
// transforms: the scrim is a STATIC `linear-gradient` paint and the button is at
// its resting position on the first frame. There is no travel to suppress, so
// the reduced-motion user and everyone else see the identical, correct result.
// Do not add an entrance here without checking that its FINAL state is the base
// style — the frozen-scroll bug elsewhere in this codebase came from encoding a
// resting position inside a keyframe.
//
// The scrim is `transparent -> var(--bg)`, so it resolves to the page ground in
// BOTH palettes and needs no hex.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { CATALOG_V2_PATH } from "./catalogChrome";

/** Chevron that points down when collapsed, up when open. A `transform` with no
 *  transition — see the header: the resting state is the whole state. */
const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 12 12"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    style={{
      color: "var(--muted)",
      flexShrink: 0,
      transform: open ? "rotate(180deg)" : undefined,
    }}
    aria-hidden
  >
    <path d="M3 4.5 L6 7.5 L9 4.5" />
  </svg>
);

/** "‹ Katalóg strojov". The back affordance below 1280, where the desktop
 *  breadcrumb band does not render. It is the SAME destination the breadcrumb's
 *  KATALÓG hop goes to (catalogChrome.tsx `CATALOG_V2_PATH`) — one route
 *  constant, so the two back paths cannot drift. */
export const CatalogDetailBackLink: React.FC = () => {
  const { translate } = useTranslation("myMachinery");
  const navigate = useNavigate();

  return (
    <div
      style={{
        padding: "0 8px",
        background: "var(--card)",
        borderBottom: "1px solid var(--border-soft)",
        flexShrink: 0,
        display: "flex",
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => navigate(CATALOG_V2_PATH)}
        style={{
          // >= 44px BOTH axes. The label clears the width; the floor is the law.
          height: 44,
          minWidth: 44,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          border: 0,
          background: "transparent",
          padding: "0 6px",
          cursor: "pointer",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          letterSpacing: "0.04em",
          color: "var(--mod-accent)",
          // NO `minWidth: 0` here, deliberately: it would override the 44px
          // floor declared above and quietly drop this control under the touch
          // law. Truncation is handled by overflow + textOverflow alone.
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <span aria-hidden>‹</span>
        {translate("myMachinery:machineCatalog")}
      </button>
    </div>
  );
};

export interface CatalogDetailSectionProps {
  title: string;
  /** Mono pill after the title — the board's "DOKUMENTÁCIA 6". */
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

/** A collapsible card section. The header row is the toggle and is full-width,
 *  so the 44px rule is met by the row rather than by a small chevron target. */
export const CatalogDetailSection: React.FC<CatalogDetailSectionProps> = ({
  title,
  count,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          minHeight: 44,
          padding: "10px 13px",
          border: 0,
          borderBottom: open ? "1px solid var(--border-soft)" : "none",
          background: "transparent",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
          // 44, NEVER 0 — the pair to `minHeight` above. It is inert today (the
          // button is `width: "100%"` in a block container, so no minimum
          // binds), but a `0` sitting beside a stated minimum is the exact
          // shape that voided the width half of the floor on TabSelect and on
          // SpecSectionsV2, twice in one sub-phase. The inner label span owns
          // the truncation; this declaration never did.
          minWidth: 44,
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: "var(--ink)",
            textTransform: "uppercase",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </span>
        {count !== undefined && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--muted)",
              background: "var(--bg-soft)",
              border: "1px solid var(--border)",
              borderRadius: 999,
              padding: "1px 7px",
              flexShrink: 0,
            }}
          >
            {count}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <Chevron open={open} />
      </button>
      {open && <div style={{ minWidth: 0 }}>{children}</div>}
    </div>
  );
};

/** One "label / mono value" row inside KĽÚČOVÉ PARAMETRE.
 *
 *  THE VALUE CARRIES ITS OWN UNIT. The board styles the unit as a separate,
 *  smaller token; `extractKeySpecs` (catalogKeySpecs.ts) joins `specValue` and
 *  `unit` into one string, deliberately, to stay byte-identical to v1 — and the
 *  desktop aside renders that same joined string. Splitting it here would mean
 *  re-parsing a value the extractor already composed, so the row prints it whole
 *  and the difference from the board is one type size on the unit. */
export const CatalogDetailSpecRow: React.FC<{
  label: string;
  value: string;
  first: boolean;
}> = ({ label, value, first }) => (
  <div
    style={{
      padding: "7px 13px",
      borderTop: first ? "none" : "1px solid var(--border-soft)",
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 11,
        color: "var(--muted)",
        textTransform: "capitalize",
        wordBreak: "break-word",
        minWidth: 0,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 13,
        fontWeight: 600,
        color: "var(--ink)",
        marginTop: 1,
        wordBreak: "break-word",
        minWidth: 0,
      }}
    >
      {value}
    </div>
  </div>
);

/** The board's bottom-pinned primary, with the gradient scrim above it. */
export const CatalogDetailStickyCta: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <div
    style={{
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      padding: "10px 14px 14px",
      // Progressive enhancement, in this order: an unsupported env() drops the
      // declaration and the 14px above still stands, so a notched phone cannot
      // put the CTA under the home indicator.
      paddingBottom: "max(14px, env(safe-area-inset-bottom))",
      background: "linear-gradient(transparent, var(--bg) 30%)",
      // The scrim is decorative; only the button inside it takes input, so the
      // list underneath stays scrollable right up to the button's edge.
      pointerEvents: "none",
    }}
  >
    <button
      type="button"
      className="btn btn-mod"
      onClick={onClick}
      style={{
        width: "100%",
        height: 48,
        minWidth: 44,
        justifyContent: "center",
        borderRadius: 11,
        fontSize: 14,
        pointerEvents: "auto",
      }}
    >
      <span aria-hidden style={{ fontSize: 16, lineHeight: 1, marginRight: 4 }}>
        +
      </span>
      {label}
    </button>
  </div>
);
